<script lang="ts">
	import Carousel from '$lib/modules/carousel/Carousel.svelte';
	import CarouselControls from '$lib/modules/carousel/CarouselControls.svelte';
	import type { OperationSummaryModel } from '@app/models/api/OperationSummaryModel';
	import FundingCard from '../cards/FundingCard.svelte';
	import { getOperations } from '@app/services/api/operations';
	import {
		OperationRegulationsEnum,
		OperationStatusEnumFR
	} from '@app/models/api/enums/OperationStatusEnum';
	import { t } from 'svelte-i18n';
	import {
		getOperationCategoryFromStoryBlock,
		getOperationSectorFromEN
	} from '@app/models/api/enums/OperationCategoryEnum';
	import CMSCallToAction from '@app/components/cms/CMSCallToAction.svelte';
	import { storyblokEditable } from '@storyblok/svelte';
	import LandingPageSectionTitles from '@app/components/cms/landing-pages/LandingPageSectionTitles.svelte';
	import { viewport } from '$lib/stores';
	import TabletSwipeableWrapper from '@app/components/layouts/TabletSwipeableWrapper.svelte';
	import ProgressIndicator from '$lib/elements/ProgressIndicator.svelte';
	import {
		getOperationFinancialInstrumentFromEN,
		getOperationFinancialInstrumentString,
		OperationFinancialInstrumentEnum,
		OperationFinancialInstrumentEnumFR
	} from '@app/models/api/enums/OperationFinancialInstrumentEnum';
	import AlertDanger from '@app/components/alerts/AlertDanger.svelte';
	import ButtonBoxPrimary from '$lib/elements/buttons/variants/ButtonBoxPrimary.svelte';
	import { urls } from '@app/urls';
	import type { CTAProps } from '@app/components/cms/types';

	export let blok: {
		title?: string;
		desc?: string;
		type: OperationRegulationsEnum.PSFP | OperationFinancialInstrumentEnum.AIF_SHARES | 'PRIVATE';
		categories?: string[];
		financial_instrument?: OperationFinancialInstrumentEnum[];
		cta?: CTAProps[];
	};

	let fundingOperations: OperationSummaryModel[];
	const regulation =
		blok.type === OperationRegulationsEnum.PSFP
			? OperationRegulationsEnum.PSFP
			: OperationRegulationsEnum.PSI;

	const getSlides = async () => {
		let categoriesParam = undefined;
		let financialInstrumentParam = undefined;

		if (blok.categories?.length && blok.type === OperationRegulationsEnum.PSFP) {
			categoriesParam = blok.categories
				.map((c: string) => getOperationSectorFromEN(getOperationCategoryFromStoryBlock(c)))
				.join();
		}
		if (blok.financial_instrument?.length && blok.type === OperationRegulationsEnum.PSFP) {
			financialInstrumentParam = blok.financial_instrument
				.map((c: OperationFinancialInstrumentEnum) => getOperationFinancialInstrumentFromEN(c))
				.join();
		}
		if (blok.type === OperationFinancialInstrumentEnum.AIF_SHARES) {
			financialInstrumentParam = OperationFinancialInstrumentEnumFR.PARTS_FIA;
		}
		if (blok.type === 'PRIVATE') {
			financialInstrumentParam = `${OperationFinancialInstrumentEnumFR.ACTIONS},${OperationFinancialInstrumentEnumFR.TITRES_PARTICIPATIFS},${OperationFinancialInstrumentEnumFR.OBLIGATIONS}`;
		}
		const res = await getOperations(
			`reglementations=${regulation}&statuts=${OperationStatusEnumFR.EN_FINANCEMENT}${
				categoriesParam ? `&categories=${categoriesParam}` : ''
			}${financialInstrumentParam ? `&instruments=${financialInstrumentParam}` : ''}`
		);

		// hide confidential & ongoing closing operations
		fundingOperations = res.data.filter(
			(o: OperationSummaryModel) => !(o.isConfidential && o.isOngoingClosing)
		);

		if (fundingOperations.length) {
			return fundingOperations.map((operation) => {
				return {
					component: FundingCard,
					props: {
						props: { ...operation }
					}
				};
			});
		}
	};

	const getFinancialInstrumentsTranslation = (
		fi: OperationFinancialInstrumentEnum[]
	): string | undefined => {
		return fi.map((e) => getOperationFinancialInstrumentString(e, $t)).join(' ou ');
	};

	const getCategoriesTranslation = (cat: string[]): string | undefined => {
		return cat
			.map((c: string) => getOperationSectorFromEN(getOperationCategoryFromStoryBlock(c)))
			.join(' ou ');
	};
</script>

{#await getSlides()}
	<div class="flex h-[500px] justify-center items-center">
		<ProgressIndicator size="huge" />
	</div>
{:then slides}
	{#if slides}
		<div class="container pt-4 pb-[64px] lg:pb-[80px]" use:storyblokEditable={blok}>
			<LandingPageSectionTitles {blok} />
			<div class="pt-4 relative">
				{#if $viewport.isDesktop}
					<Carousel {slides} controls swipe breakpoints spin>
						<div class="absolute right-0 top-1/2 -translate-y-1/2 z-1" slot="controls">
							<CarouselControls next rounded />
						</div>
					</Carousel>
				{:else if fundingOperations && fundingOperations.length}
					<TabletSwipeableWrapper classNames="flex gap-3">
						{#each fundingOperations as o}
							<FundingCard props={o} />
						{/each}
					</TabletSwipeableWrapper>
				{/if}
				{#if blok.cta?.length}
					<div class="text-center pt-4">
						<CMSCallToAction props={blok.cta[0]} />
					</div>
				{/if}
			</div>
		</div>
	{:else}
		<div class="flex justify-center py-5">
			<div class="md:w-1/2 text-center">
				<p>
					{$t('portfolio.noProjectsOfThisType', {
						values: {
							type: `${blok.categories?.length ? getCategoriesTranslation(blok.categories) : ''} ${blok.categories?.length && blok.financial_instrument?.length ? ' et ' : ''} ${blok.financial_instrument?.length ? getFinancialInstrumentsTranslation(blok.financial_instrument) : ''}`
						}
					})}
				</p>

				<ButtonBoxPrimary props={{ label: $t('allProjects'), href: urls.PROJECTS[blok.type] }} />
			</div>
		</div>
	{/if}
{:catch e}
	<AlertDanger label={$t('account.dashboard.OCA.initSignatureFail')} />
{/await}

<style lang="postcss">
	:global (.card) {
		@apply w-[80vw] flex-shrink-0 md:w-[45vw] lg:w-full;
	}
</style>
